import { useCallback, useEffect, useMemo, useState } from "react";

const DEFAULT_THRESHOLD_MULTIPLIER = 1.75;
const DEFAULT_MIN_THRESHOLD_PX = 720;

function isScrollableElement(value) {
  return Boolean(value && typeof value.scrollTo === "function");
}

function getViewportHeight(target) {
  if (
    target &&
    target !== window &&
    Number.isFinite(target.clientHeight) &&
    target.clientHeight > 0
  ) {
    return target.clientHeight;
  }

  if (typeof window === "undefined") {
    return 0;
  }

  return window.innerHeight || document.documentElement?.clientHeight || 0;
}

function getScrollTop(target) {
  if (target && target !== window) {
    return Number(target.scrollTop) || 0;
  }

  if (typeof window === "undefined") {
    return 0;
  }

  return (
    window.scrollY ||
    window.pageYOffset ||
    document.documentElement?.scrollTop ||
    0
  );
}

function preferredScrollBehavior() {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return "auto";
  }

  return "smooth";
}

export default function useScrollToTopButton({
  targetRef,
  thresholdMultiplier = DEFAULT_THRESHOLD_MULTIPLIER,
  minThresholdPx = DEFAULT_MIN_THRESHOLD_PX,
} = {}) {
  const [isVisible, setIsVisible] = useState(false);

  const resolveTarget = useCallback(() => {
    const currentTarget = targetRef?.current;
    if (isScrollableElement(currentTarget)) {
      return currentTarget;
    }

    if (typeof window === "undefined") {
      return null;
    }

    return window;
  }, [targetRef]);

  const thresholdPx = useMemo(() => {
    const target = resolveTarget();
    const viewportHeight = getViewportHeight(target);
    return Math.max(
      minThresholdPx,
      Math.round(viewportHeight * thresholdMultiplier)
    );
  }, [minThresholdPx, resolveTarget, thresholdMultiplier]);

  useEffect(() => {
    const target = resolveTarget();
    if (!target) {
      return undefined;
    }

    let frameId = 0;

    const syncVisibility = () => {
      frameId = 0;
      const currentTarget = resolveTarget();
      const nextVisible = getScrollTop(currentTarget) > thresholdPx;
      setIsVisible((currentVisible) =>
        currentVisible === nextVisible ? currentVisible : nextVisible
      );
    };

    const requestSync = () => {
      if (frameId !== 0) {
        return;
      }

      frameId = window.requestAnimationFrame(syncVisibility);
    };

    syncVisibility();
    target.addEventListener("scroll", requestSync, { passive: true });

    if (typeof window !== "undefined") {
      window.addEventListener("resize", requestSync);
      window.addEventListener("orientationchange", requestSync);
    }

    return () => {
      target.removeEventListener("scroll", requestSync);
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", requestSync);
        window.removeEventListener("orientationchange", requestSync);
        if (frameId !== 0) {
          window.cancelAnimationFrame(frameId);
        }
      }
    };
  }, [resolveTarget, thresholdPx]);

  const scrollToTop = useCallback(() => {
    const target = resolveTarget();
    if (!target) {
      return;
    }

    const behavior = preferredScrollBehavior();

    if (target === window) {
      window.scrollTo({ top: 0, behavior });
      return;
    }

    target.scrollTo({ top: 0, behavior });
  }, [resolveTarget]);

  return {
    isVisible,
    scrollToTop,
    thresholdPx,
  };
}
